/**
 * Global behaviour: slow reveals, subtle parallax, and side drawers (filters, cart).
 * Motion is skipped under prefers-reduced-motion and when disabled in theme settings.
 */
(() => {
  const root = document.documentElement;
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  // The accessibility menu's "stop animations" counts as reduced motion.
  const reducedMotion = {
    get matches() {
      return reducedMotionQuery.matches || root.classList.contains('a11y-motion');
    },
  };

  function initReveal() {
    const items = document.querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!items.length) return;

    if (!root.hasAttribute('data-motion-reveal') || reducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    items.forEach((item) => observer.observe(item));
  }

  function initParallax() {
    const items = [...document.querySelectorAll('[data-parallax]')];
    if (!items.length || !root.hasAttribute('data-motion-parallax') || reducedMotion.matches) return;

    let ticking = false;
    const update = () => {
      const viewport = window.innerHeight;
      items.forEach((item) => {
        const rect = item.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > viewport) return;
        const strength = Number(item.dataset.parallax) || 0.08;
        const offset = (rect.top + rect.height / 2 - viewport / 2) * strength;
        item.style.setProperty('--parallax-offset', `${offset.toFixed(1)}px`);
      });
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  }


  /**
   * Side drawers built on <dialog>: [data-open-drawer="DialogId"] opens, [data-close-drawer] closes.
   * The header manages its own drawers. Exposed as window.themeDrawer for the cart drawer.
   */
  const drawer = {
    open(dialog, trigger) {
      if (!dialog || dialog.open) return;
      dialog.drawerTrigger = trigger || document.activeElement;
      dialog.drawerTrigger?.setAttribute?.('aria-expanded', 'true');
      dialog.showModal();
      requestAnimationFrame(() => dialog.classList.add('is-visible'));
    },
    close(dialog) {
      if (!dialog?.open) return;
      dialog.classList.remove('is-visible');
      const finish = () => {
        if (!dialog.open) return;
        dialog.close();
        dialog.drawerTrigger?.setAttribute?.('aria-expanded', 'false');
        dialog.drawerTrigger?.focus?.();
      };
      if (reducedMotion.matches) return finish();
      dialog.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 500);
    },
  };
  window.themeDrawer = drawer;

  /**
   * Language links point at the same path under the other locale root. Product / collection / page handles
   * can be translated, so prefer Shopify's own hreflang alternate (its path, on the current host), and keep
   * the query string (filters, sorting), which Liquid can't read.
   */
  window.localizeLink = (link) => {
    const lang = link.getAttribute('hreflang') || link.closest('[data-language]')?.dataset.language;
    const alternate = lang && document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`);
    if (alternate) {
      try {
        link.pathname = new URL(alternate.href).pathname;
      } catch (error) {}
    }
    if (location.search) link.search = location.search;
  };
  window.themeReveal = initReveal;

  // Any explicit language choice ends the first-visit Hebrew redirect (layout/theme.liquid).
  document.addEventListener(
    'click',
    (event) => {
      if (!event.target.closest('.localization__language, .language-suggestion__action')) return;
      try {
        localStorage.setItem('kc-locale-chosen', 'manual');
      } catch (error) {}
    },
    true
  );

  document.addEventListener('click', (event) => {
    if (event.target.closest('header-component')) return;
    const opener = event.target.closest('[data-open-drawer]');
    if (opener) {
      event.preventDefault();
      drawer.open(document.getElementById(opener.dataset.openDrawer), opener);
      return;
    }
    const dialog = event.target.closest('dialog.drawer--side');
    if (!dialog) return;
    if (event.target === dialog || event.target.closest('[data-close-drawer]')) drawer.close(dialog);
  });

  document.addEventListener(
    'cancel',
    (event) => {
      if (!event.target.matches?.('dialog.drawer--side')) return;
      event.preventDefault();
      drawer.close(event.target);
    },
    true
  );

  // Policy bodies are store text rendered by Shopify (no Liquid hook): give their headings
  // a clean outline under the page h1 (h3 sections become h2), as snippets/rich-text does elsewhere.
  function fixPolicyHeadings() {
    const body = document.querySelector('.shopify-policy__body');
    if (!body) return;
    const headings = [...body.querySelectorAll('h1, h2, h3, h4, h5, h6')];
    if (!headings.length) return;
    const top = Math.min(...headings.map((h) => Number(h.tagName[1])));
    const offset = 2 - top;
    let previous = 1;
    headings.forEach((h) => {
      // Shift, then never skip a level after the previous heading.
      const level = Math.min(6, previous + 1, Math.max(2, Number(h.tagName[1]) + offset));
      previous = level;
      if (level === Number(h.tagName[1])) return;
      const next = document.createElement(`h${level}`);
      [...h.attributes].forEach((a) => next.setAttribute(a.name, a.value));
      next.append(...h.childNodes);
      h.replaceWith(next);
    });
  }

  function init() {
    initReveal();
    initParallax();
  }

  fixPolicyHeadings();
  init();
  document.addEventListener('shopify:section:load', init);
})();
